import React from 'react';
import {
    CaretRightOutlined,
    CloseCircleFilled,
    ExclamationCircleFilled,
    ThunderboltFilled,
    WarningFilled,
} from '@ant-design/icons';
import { StatusNode } from '../StatusNode';
import { AppPlatform, AppStoreType, ReleaseStepStatus, ReleaseStepType, WorkflowEnabledSteps } from '../../types';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import {
    isHalfFilledStep,
    isInnerFilledStep,
    isPhasedReleasePaused,
    isReleaseStepType,
    statusColorForReleaseStepStatus,
} from '../../helpers';
import { displayTextForReleaseStepType } from '../../helpers/displayHelpers';
import { ROUTER_REDIRECT } from '../../helpers/routeHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { StepItemLinkContainer, StepItemText, StepItemTextContainer } from './StepItem';
import Tag from 'antd/lib/tag';
import Popover from 'antd/lib/popover';
import { RolloutStepRunStatusData, StepRunStatusData } from '@/types/stepRunStatus';
import Collapse from 'antd/lib/collapse';
import Tooltip from 'antd/lib/tooltip';
import { RolloutBadge } from './RolloutBadge';
import { Nullable } from '@/typescriptHelpers';

interface ReleaseStepsSidebarSingleNodeProps {
    name: string;
    status: ReleaseStepStatus;
    stepArchetype: ReleaseStepType;
    isActive: boolean;
    isExpanded: Nullable<boolean>;
    setIsExpanded: Nullable<(isExpanded: boolean) => void>;
    onClick: () => void;
    isNonBlocking: boolean;
    isPlanRestricted: boolean;
    isFromScraper: boolean;
    shouldShowTail: boolean;
    shouldShowHead: boolean;
    pendingChecklistItemCount: number;
    hasEnabledLifecycleAutomation: boolean;
    hasEnabledPendingLifecycleAutomation: boolean;
    hasEnabledFailedLifecycleAutomation: boolean;
    hasEnabledBlockedLifecycleAutomation: boolean;
    shouldShowTagNeededWarning: boolean;
    isHalfFilledStep: boolean;
    isInnerFilledStep: boolean;
    subStepCount?: number;
    isSubStep?: boolean;
    shouldHighlightOnHover?: boolean;
    isRolloutPaused?: boolean;
}

const ReleaseStepsSidebarSingleNode: React.FC<ReleaseStepsSidebarSingleNodeProps> = ({
    name,
    status,
    stepArchetype,
    isActive,
    isExpanded,
    setIsExpanded,
    onClick,
    isNonBlocking,
    isPlanRestricted,
    isFromScraper,
    shouldShowTail,
    shouldShowHead,
    pendingChecklistItemCount,
    hasEnabledLifecycleAutomation,
    hasEnabledPendingLifecycleAutomation,
    hasEnabledFailedLifecycleAutomation,
    hasEnabledBlockedLifecycleAutomation,
    shouldShowTagNeededWarning,
    isHalfFilledStep,
    isInnerFilledStep,
    isSubStep,
    subStepCount,
    shouldHighlightOnHover,
    isRolloutPaused,
}) => {
    let showAsNonBlocking = isNonBlocking || isPlanRestricted;
    let itemColor = statusColorForReleaseStepStatus(status);
    let outerBorderColor: string;
    let innerFilled = isInnerFilledStep;

    if (isFromScraper) {
        // release step should always show as green, other steps as disabled/nonblocking
        if (isReleaseStepType(stepArchetype)) {
            const readyStatusColor = statusColorForReleaseStepStatus(ReleaseStepStatus.Ready);

            itemColor = readyStatusColor;
            // outerBorderColor = readyStatusColor;
            innerFilled = false; // release step should show as totally filled
        } else {
            showAsNonBlocking = true;
            itemColor = antGrey.grey6;
            outerBorderColor = antGrey.grey5;
            innerFilled = true;
        }
    } else if (showAsNonBlocking) {
        itemColor = antGrey.grey6;
        outerBorderColor = antGrey.grey6;
        innerFilled = true;
    }

    const node = (
        <StatusNode
            statusColor={itemColor}
            showTail={shouldShowTail}
            showHead={shouldShowHead}
            halfFilled={isHalfFilledStep}
            innerFilled={innerFilled}
            outerBorderColor={outerBorderColor}
            shouldShowMiniVersion={isSubStep}
        />
    );

    const releaseStepIsActive = status !== ReleaseStepStatus.Inactive;

    const automationIcon =
        shouldShowTagNeededWarning ?
            <Tooltip title="This release needs to be tagged.">
                <WarningFilled style={{ color: runwayColors.statusYellow }} />
            </Tooltip>
        : hasEnabledFailedLifecycleAutomation ? <CloseCircleFilled style={{ color: runwayColors.statusRed }} />
        : hasEnabledBlockedLifecycleAutomation ? <ExclamationCircleFilled style={{ color: runwayColors.statusRed }} />
        : hasEnabledPendingLifecycleAutomation ? <ThunderboltFilled style={{ color: runwayColors.info }} />
        : hasEnabledLifecycleAutomation ?
            <ThunderboltFilled style={{ color: isActive ? antGrey.grey1 : antGrey.grey6 }} />
        :   null;

    const needsAdditionalSpacing = pendingChecklistItemCount > 0 && releaseStepIsActive;

    return (
        <div
            style={{
                cursor: 'pointer',
            }}
            className={shouldHighlightOnHover ? 'steps-sidebar-expanded' : null}
            onClick={onClick}
        >
            <StepItemLinkContainer isActive={isActive} isSubStep={isSubStep} hasIcon={needsAdditionalSpacing}>
                {pendingChecklistItemCount > 0 && releaseStepIsActive && (
                    <Tag
                        style={{
                            color: runwayColors.black,
                            fontSize: 14,
                            borderColor: antGrey.grey6,
                            borderWidth: 1,
                            backgroundColor: antGrey.grey1,
                        }}
                    >
                        {pendingChecklistItemCount}
                    </Tag>
                )}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        minWidth: 0,
                    }}
                >
                    {automationIcon}
                    {!isSubStep && subStepCount && (
                        <CaretRightOutlined
                            style={{
                                color: antGrey.grey8,
                                fontSize: 16,
                                padding: 10,
                                marginRight: -10,
                            }}
                            onClick={(e) => {
                                if (setIsExpanded) {
                                    e.stopPropagation();
                                    setIsExpanded(!isExpanded);
                                }
                            }}
                            rotate={
                                (
                                    setIsExpanded ? isExpanded : isActive
                                ) ?
                                    90
                                :   0
                            }
                        />
                    )}
                    {stepArchetype === ReleaseStepType.Rollout && <RolloutBadge isRolloutPaused={isRolloutPaused} />}
                    <StepItemTextContainer>
                        <StepItemText shouldBoldText={isActive && !isSubStep} nonBlocking={showAsNonBlocking}>
                            {name}
                        </StepItemText>
                    </StepItemTextContainer>
                    {node}
                </div>
            </StepItemLinkContainer>
        </div>
    );
};

interface ReleaseStepsSidebarNodeProps {
    stepRunsSummaryRoute: Nullable<string>;
    appPlatform: AppPlatform | AppPlatform[];
    appStoreType: AppStoreType | AppStoreType[];
    isFirstNode: boolean;
    isLastNode: boolean;
    isTagNeeded: boolean;
    isNonBlocking: boolean;
    isPlanRestricted: boolean;
    isFromScraper: boolean;
    stepRunStatusDatum: StepRunStatusData[];
    stepArchetype: ReleaseStepType;
    allStepRunPaths: string[];
    allStepRunTabPaths: string[];
    isActive: boolean;
    previousNodeHasSubStepsAndIsExpanded: boolean;
    workflowEnabledSteps: WorkflowEnabledSteps;
    isWorkflowsCIStepEnabled: boolean;
    combinedStatus: ReleaseStepStatus;
    customArchetypeGroupName: string;
}

const COLLAPSIBLE_KEY = `1`;
export const ReleaseStepsSidebarNode: React.FC<ReleaseStepsSidebarNodeProps> = ({
    stepRunsSummaryRoute,
    workflowEnabledSteps,
    stepRunStatusDatum,
    stepArchetype,
    appPlatform,
    appStoreType,
    isLastNode,
    isFirstNode,
    isTagNeeded,
    isNonBlocking,
    isPlanRestricted,
    isFromScraper,
    allStepRunPaths,
    allStepRunTabPaths,
    isActive,
    previousNodeHasSubStepsAndIsExpanded,
    isWorkflowsCIStepEnabled,
    combinedStatus,
    customArchetypeGroupName,
}) => {
    const history = useHistory();
    const location = useLocation();
    const firstStepRunStatusData = stepRunStatusDatum[0];
    const hasMoreThanOneStatusData = stepRunStatusDatum.length > 1;

    const [isExpanded, setIsExpanded] = React.useState(isActive);

    const stepArchetypeName =
        customArchetypeGroupName ? customArchetypeGroupName : (
            displayTextForReleaseStepType(stepArchetype, appPlatform, appStoreType, isWorkflowsCIStepEnabled)
        );

    const mergedIsHalfFilledStep = stepRunStatusDatum.some(isHalfFilledStep);
    const mergedIsInnerFilledStep = stepRunStatusDatum.some(isInnerFilledStep);
    const mergedChecklistItemCount = stepRunStatusDatum.reduce(
        (acc, stepRunStatusData) => acc + stepRunStatusData.pendingChecklistItemCount,
        0,
    );
    const mergedHasEnabledLifecycleAutomation = stepRunStatusDatum.some(
        (stepRunStatusData) => stepRunStatusData.hasEnabledLifecycleAutomation,
    );
    const mergedHasPendingLifecycleAutomation = stepRunStatusDatum.some(
        (stepRunStatusData) => stepRunStatusData.hasEnabledPendingLifecycleAutomation,
    );
    const mergedHasFailedLifecycleAutomation = stepRunStatusDatum.some(
        (stepRunStatusData) => stepRunStatusData.hasEnabledFailedLifecycleAutomation,
    );
    const mergedHasBlockedLifecycleAutomation = stepRunStatusDatum.some(
        (stepRunStatusData) => stepRunStatusData.hasEnabledBlockedLifecycleAutomation,
    );

    const shouldBeExpanded = stepRunsSummaryRoute ? isExpanded : isActive; // for summary views, we have now an independent collapse state. For legacy, if we are on any of the steps inside the archetype group, expand the collapsible

    const isArchetypeWorkflowsEnabled = workflowEnabledSteps[stepArchetype];
    const baseNodeName =
        isArchetypeWorkflowsEnabled ? firstStepRunStatusData?.name || stepArchetypeName : stepArchetypeName;

    const baseNode = (
        <ReleaseStepsSidebarSingleNode
            stepArchetype={stepArchetype}
            isNonBlocking={isNonBlocking}
            isPlanRestricted={isPlanRestricted}
            isFromScraper={isFromScraper}
            shouldShowTail={!isLastNode && (!hasMoreThanOneStatusData || !shouldBeExpanded)}
            shouldShowHead={!isFirstNode && !previousNodeHasSubStepsAndIsExpanded}
            shouldShowTagNeededWarning={isTagNeeded}
            name={hasMoreThanOneStatusData ? stepArchetypeName : baseNodeName}
            isActive={isActive}
            isExpanded={stepRunsSummaryRoute ? isExpanded : undefined}
            setIsExpanded={stepRunsSummaryRoute ? setIsExpanded : undefined}
            onClick={() => {
                if (allStepRunTabPaths.length > 1 && stepRunsSummaryRoute) {
                    if (!isActive) {
                        history.push(stepRunsSummaryRoute, { [ROUTER_REDIRECT]: true });
                    } else {
                        setIsExpanded(!isExpanded);
                    }
                } else {
                    history.push(allStepRunTabPaths[0], { [ROUTER_REDIRECT]: true });
                }
            }}
            status={combinedStatus}
            pendingChecklistItemCount={mergedChecklistItemCount}
            hasEnabledLifecycleAutomation={mergedHasEnabledLifecycleAutomation}
            hasEnabledPendingLifecycleAutomation={mergedHasPendingLifecycleAutomation}
            hasEnabledFailedLifecycleAutomation={mergedHasFailedLifecycleAutomation}
            hasEnabledBlockedLifecycleAutomation={mergedHasBlockedLifecycleAutomation}
            isHalfFilledStep={mergedIsHalfFilledStep}
            isInnerFilledStep={mergedIsInnerFilledStep}
            subStepCount={hasMoreThanOneStatusData ? stepRunStatusDatum.length : undefined}
        />
    );

    if (hasMoreThanOneStatusData) {
        const allSubSteps = stepRunStatusDatum.map((stepRunStatusData, index) => {
            const stepName =
                isArchetypeWorkflowsEnabled ? (stepRunStatusData.name ?? stepArchetypeName) : stepArchetypeName;

            const path = allStepRunPaths[index];

            const isActive = !!matchPath(location.pathname, path);

            const isRolloutPaused =
                stepRunStatusData.extraData &&
                isPhasedReleasePaused((stepRunStatusData as RolloutStepRunStatusData).extraData.phasedReleaseState);

            return (
                <ReleaseStepsSidebarSingleNode
                    key={stepRunStatusData.stepRunId + index}
                    name={stepName}
                    status={stepRunStatusData.status}
                    stepArchetype={stepArchetype}
                    isActive={isActive}
                    isExpanded={undefined}
                    setIsExpanded={undefined}
                    onClick={() => {
                        history.push(path, { [ROUTER_REDIRECT]: true });
                    }}
                    shouldHighlightOnHover={!shouldBeExpanded}
                    isNonBlocking={isNonBlocking}
                    isPlanRestricted={isPlanRestricted}
                    isFromScraper={isFromScraper}
                    shouldShowTail={false}
                    shouldShowHead={false}
                    pendingChecklistItemCount={stepRunStatusData.pendingChecklistItemCount}
                    hasEnabledLifecycleAutomation={stepRunStatusData.hasEnabledLifecycleAutomation}
                    hasEnabledPendingLifecycleAutomation={stepRunStatusData.hasEnabledPendingLifecycleAutomation}
                    hasEnabledFailedLifecycleAutomation={stepRunStatusData.hasEnabledFailedLifecycleAutomation}
                    hasEnabledBlockedLifecycleAutomation={stepRunStatusData.hasEnabledBlockedLifecycleAutomation}
                    shouldShowTagNeededWarning={isTagNeeded}
                    isHalfFilledStep={isHalfFilledStep(stepRunStatusData)}
                    isInnerFilledStep={isInnerFilledStep(stepRunStatusData)}
                    isSubStep
                    isRolloutPaused={isRolloutPaused}
                />
            );
        });
        const margin = 16;
        const popoverNormalWidth = 279 - margin;
        return (
            <Collapse
                ghost
                activeKey={shouldBeExpanded ? COLLAPSIBLE_KEY : null}
                expandIcon={() => null}
                collapsible="header"
                destroyInactivePanel={false}
                className="ant-collapse-sidebar-node"
                items={[
                    {
                        key: COLLAPSIBLE_KEY,
                        label:
                            shouldBeExpanded ? baseNode : (
                                <Popover
                                    content={
                                        <div
                                            style={{
                                                width: popoverNormalWidth,
                                            }}
                                        >
                                            {allSubSteps}
                                        </div>
                                    }
                                    overlayInnerStyle={{
                                        padding: `12px 0`,
                                    }}
                                    arrow={false}
                                    autoAdjustOverflow
                                    placement="bottom"
                                    trigger="hover"
                                >
                                    <div>{baseNode}</div>
                                </Popover>
                            ),
                        children: allSubSteps,
                    },
                ]}
            />
        );
    }

    return baseNode;
};
